if (!customElements.get('product-add-to-cart-sticky')) {
    class ProductAddToCartSticky extends HTMLElement {
      constructor() {
        super();
        this.handleScroll = this.handleScroll.bind(this);
        this.sectionId = this.dataset.section;
        this.variantChangeUnsubscriber = undefined;
      }
  
      connectedCallback() {
        this.targetElement = document.querySelector('[id^="ProductSubmitButton-"]');
        this.footerElement = document.querySelector('footer');
        this.stickyAtcVariantInput = this.querySelector('.product-variant-id');
  
        window.addEventListener("scroll", this.handleScroll);
        
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange.bind(this)
        );
  
        this.handleScroll();
      }
  
      disconnectedCallback() {
        window.removeEventListener("scroll", this.handleScroll);
        
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }
  
      getAbsoluteTop(element) {
        if (!element) return 0;
        let top = 0;
        while (element) {
          top += element.offsetTop || 0;
          element = element.offsetParent;
        }
        return top;
      }
  
      handleScroll() {
        // On mobile the bar is visible from page load until the user scrolls
        // past the top of the related-products carousel header ("You May Also
        // Like / Recently Viewed"). Cached on first hit so we don't pay the
        // DOM lookup on every scroll tick.
        if (window.innerWidth <= 767) {
          if (this.recCarouselTop === undefined) {
            const rec = document.querySelector('.custom-tab-header')
              || document.querySelector('.section-related-products');
            this.recCarouselTop = rec ? this.getAbsoluteTop(rec) : Infinity;
          }
          if (window.scrollY + window.innerHeight > this.recCarouselTop) {
            this.classList.remove('show');
          } else {
            this.classList.add('show');
          }
          return;
        }

        if (!this.targetElement || !this.footerElement) return;

        const targetTopPosition = this.getAbsoluteTop(this.targetElement);
        const footerTopPosition = this.getAbsoluteTop(this.footerElement);
        const isTargetElementOutOfView = targetTopPosition < window.scrollY;
        const isFooterInView = window.scrollY + window.innerHeight > footerTopPosition;

        if (isTargetElementOutOfView && !isFooterInView) {
          this.classList.add('show');
        } else {
          this.classList.remove('show');
        }
      }
  
      handleVariantChange({ data: { sectionId, html, variant } }) {
        const mainSectionId = this.sectionId.replace(/^template--\d+__/, '');
        const eventSectionId = sectionId.replace(/^template--\d+__/, '');
        
        if (mainSectionId !== eventSectionId) return;
  
        // Update variant input
        if (this.stickyAtcVariantInput) {
          this.stickyAtcVariantInput.value = variant?.id || '';
          this.stickyAtcVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
  
        this.updateStickyOptionsFromHTML(sectionId, html);
      }
  
      updateStickyOptionsFromHTML(sectionId, html) {
        const sourceOptionsContainer = html.querySelector('.selected-variant-details .options-container');
        const destinationOptionsContainer = this.querySelector('.selected-variant-details .options-container');
  
        if (!sourceOptionsContainer || !destinationOptionsContainer) return;
  
        const sourceOptions = sourceOptionsContainer.querySelectorAll('.selected-variant-option');
        const destinationOptions = destinationOptionsContainer.querySelectorAll('.selected-variant-option');
  
        sourceOptions.forEach((sourceOption, index) => {
          if (destinationOptions[index]) {
            destinationOptions[index].textContent = sourceOption.textContent;
          }
        });
      }
    }
  
    customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
  }
  
/* =============================================================
   EDITED BY CLAUDE — Mar 2026
   WHAT: Mobile size-picker panel for the sticky ATC bar.
         On mobile (≤767px) tapping "Add to Cart" opens a
         quick-buy-style drawer instead of directly submitting.
         The drawer shows variant options; confirming a size
         updates the sticky form's variant ID and submits.
         Desktop behaviour (direct form submit) is unchanged.
 
   ============================================================= */

(function () {
  'use strict';

  /* ── helpers ── */
  function isMobile() {
    return window.innerWidth <= 767;
  }

  /* ── StickyPanel class ──
     One instance per product-add-to-cart-sticky element. */
  class StickyPanel {
    constructor(root) {
      this.root       = root;
      this.sectionId  = root.dataset.section;
      this.sid        = this.sectionId;

      // Elements
      this.trigger    = root.querySelector('#sticky-mobile-trigger-' + this.sid);
      this.overlay    = root.querySelector('#sticky-overlay-' + this.sid);
      this.panel      = root.querySelector('#sticky-panel-' + this.sid);
      this.closeBtn   = root.querySelector('#sticky-panel-close-' + this.sid);
      this.confirmBtn = root.querySelector('#sticky-panel-confirm-' + this.sid);
      this.sizeGrid   = root.querySelector('#sticky-panel-options-' + this.sid);

      // The hidden variant input inside the sticky product-form
      this.variantInput = root.querySelector('.product-variant-id');

      // Actual submit button (used to trigger product-form.js submission)
      this.submitBtn  = root.querySelector('#StickyProductSubmitButton-' + this.sid);

      // Variant data embedded as JSON in the snippet
      const jsonEl    = root.querySelector('#sticky-variants-' + this.sid);
      this.variants   = jsonEl ? JSON.parse(jsonEl.textContent) : [];

      // Track currently selected option values (mirrors the page picker)
      // Initialise from the hidden input's current value
      this._syncOptionsFromVariantId(
        this.variantInput ? parseInt(this.variantInput.value, 10) : null
      );

      this._chosenSize = null; // value string of the size the user picked

      this._bindEvents();
      this._markAvailability();
    }

    /* ── bind DOM events ── */
    _bindEvents() {
      if (this.trigger) {
        this.trigger.addEventListener('click', () => {
          // Notify mode: the delegated listener in buy-buttons.liquid opens
          // AMP's popup — skip panel-open so the two don't stack.
          if (this.trigger.classList.contains('product-form__notify')) return;
          if (isMobile()) this.open();
        });
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.confirmBtn) {
        this.confirmBtn.addEventListener('click', () => this._handleConfirm());
      }
      if (this.sizeGrid) {
        this.sizeGrid.addEventListener('click', (e) => {
          const btn = e.target.closest('.sticky-sz-btn');
          // Allow selecting sold-out sizes (so the confirm can flip to
          // Notify me); only block truly missing combinations.
          if (btn && !btn.classList.contains('sp-missing') && !btn.disabled) {
            this._selectSize(btn);
          }
        });
      }

      /* Keyboard: close on Escape */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    /* ── open / close ── */
    open() {
      if (!this.panel) return;
      this.panel.classList.add('sp-open');
      this.overlay.classList.add('sp-open');
      document.body.classList.add('sticky-panel-open');
      /* Re-mark availability each time we open in case variant changed */
      this._markAvailability();
      /* Pre-select the currently active size if one is already chosen */
      this._preselectCurrentSize();
    }

    close() {
      if (!this.panel) return;
      this.panel.classList.remove('sp-open');
      this.overlay.classList.remove('sp-open');
      document.body.classList.remove('sticky-panel-open');
    }

    /* ── size selection ── */
    _selectSize(btn) {
      /* deselect previous */
      this.sizeGrid.querySelectorAll('.sticky-sz-btn').forEach(b => b.classList.remove('sp-chosen'));
      btn.classList.add('sp-chosen');
      this._chosenSize = btn.dataset.value;

      /* update "Size: S" label */
      const optIdx = btn.dataset.optionIndex;
      const label  = this.root.querySelector('#sticky-chosen-label-' + this.sid + '-' + optIdx);
      if (label) label.textContent = this._chosenSize;

      /* find + update variant */
      const variant = this._findVariant(optIdx, this._chosenSize);
      if (variant && this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = false;
      }

      /* Flip confirm button between ATC and Notify me based on
         this variant's availability */
      this._updateConfirmMode(variant);
    }

    /* ── flip confirm button between ATC and Notify-me ──
       When the chosen variant is sold out, add `.sticky-notify-btn` so the
       delegated listener in buy-buttons.liquid forwards the click to AMP
       (hidden #BIS_trigger.action_button → opens the BIS popup iframe). */
    _updateConfirmMode(variant) {
      if (!this.confirmBtn) return;
      const soldOut   = !!(variant && !variant.available);
      const atcText   = this.confirmBtn.dataset.atcText    || 'Add to Cart';
      const notifyTxt = this.confirmBtn.dataset.notifyText || 'Notify me';
      if (soldOut) {
        this.confirmBtn.classList.add('sticky-notify-btn');
        this.confirmBtn.dataset.mode      = 'notify';
        this.confirmBtn.dataset.variantId = variant.id;
        this.confirmBtn.textContent       = notifyTxt;
      } else {
        this.confirmBtn.classList.remove('sticky-notify-btn');
        this.confirmBtn.dataset.mode = 'atc';
        this.confirmBtn.textContent  = atcText;
      }
    }

    /* ── confirm / add to cart ── */
    _handleConfirm() {
      if (!this._chosenSize) {
        /* flash the confirm button red if no size picked */
        this.confirmBtn.style.background = '#ea2122';
        const orig = this.confirmBtn.textContent;
        this.confirmBtn.textContent = 'Please select a size';
        setTimeout(() => {
          this.confirmBtn.style.background = '';
          this.confirmBtn.textContent = orig;
        }, 1600);
        return;
      }

      /* Notify mode: the delegated listener in buy-buttons.liquid will
         forward this same click event to AMP's popup. We just close the
         panel and bail out of the ATC submit path. */
      if (this.confirmBtn.dataset.mode === 'notify') {
        this.close();
        return;
      }

      /* Ensure the variant input is enabled before submitting */
      if (this.variantInput) this.variantInput.disabled = false;

      /* Submit via form.requestSubmit() rather than clicking the submit
         button directly — the sticky submit may carry `.product-form__notify`
         (sold-out-variant PDP), and clicking it would be intercepted by the
         delegated notify listener in buy-buttons.liquid. requestSubmit
         fires the form's submit event with no intermediate click. */
      const form = this.submitBtn && this.submitBtn.form;
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(this.submitBtn);
      } else if (this.submitBtn) {
        this.submitBtn.click();
      }

      this.close();
    }

    /* ── variant matching ──
       Given a size value at optionIndex, find the first variant
       that matches both the new size AND the current selections
       for all other options. */
    _findVariant(sizeOptionIndex, sizeValue) {
      sizeOptionIndex = parseInt(sizeOptionIndex, 10);
      return this.variants.find(v => {
        return v.options.every((val, idx) => {
          if (idx === sizeOptionIndex) return val === sizeValue;
          /* for all other options, match the currently selected value */
          return this._currentOptions[idx]
            ? val === this._currentOptions[idx]
            : true;
        });
      }) || null;
    }

    /* ── sync current option selections from a variant ID ── */
    _syncOptionsFromVariantId(variantId) {
      const variant = this.variants.find(v => v.id === variantId);
      this._currentOptions = variant ? variant.options.slice() : [];
    }

    /* ── update non-size option labels (e.g. Colour) ── */
    _updateOptionLabels(options) {
      this._currentOptions = options.slice();
      /* update informational labels in the panel */
      const labels = this.root.querySelectorAll('.sticky-panel-attr[data-option-index]');
      labels.forEach(el => {
        const idx = parseInt(el.dataset.optionIndex, 10);
        const valEl = el.querySelector('.sp-opt-val');
        if (valEl && options[idx] != null) {
          valEl.textContent = options[idx];
        }
      });
      /* re-mark which sizes are available for the new option selection */
      this._markAvailability();
    }

    /* ── mark size buttons available/unavailable ──
       A size is available if at least one variant with that size value
       (and the current other-option selections) is available. */
    _markAvailability() {
      if (!this.sizeGrid) return;
      const optIdx = parseInt(this.sizeGrid.dataset.optionIndex, 10);
      this.sizeGrid.querySelectorAll('.sticky-sz-btn').forEach(btn => {
        const sizeVal = btn.dataset.value;
        /* Any variant that matches this size AND current other options? */
        const matchesOtherOpts = (v) => v.options.every((val, idx) => {
          if (idx === optIdx) return true;
          return this._currentOptions[idx] ? val === this._currentOptions[idx] : true;
        });
        const exists = this.variants.some(v => v.options[optIdx] === sizeVal && matchesOtherOpts(v));
        const available = this.variants.some(v => v.options[optIdx] === sizeVal && v.available && matchesOtherOpts(v));
        /* `sp-out` — visual strike (sold-out OR missing). Kept for styling.
           `sp-missing` — combination doesn't exist at all; block selection.
           Sold-out-but-existing stays clickable so the user can pick it
           and have the confirm button flip to "Notify me". */
        btn.classList.toggle('sp-out', !available);
        btn.classList.toggle('sp-missing', !exists);
        btn.disabled = !exists;
      });
    }

    /* ── pre-select the size that's currently active on the page ── */
    _preselectCurrentSize() {
      if (!this.sizeGrid || !this._currentOptions.length) return;
      const optIdx = parseInt(this.sizeGrid.dataset.optionIndex, 10);
      const currentVal = this._currentOptions[optIdx];
      if (!currentVal) return;
      const btn = this.sizeGrid.querySelector('.sticky-sz-btn[data-value="' + CSS.escape(currentVal) + '"]');
      /* Preselect sold-out-but-existing sizes too, so the confirm button
         lands in Notify mode for users who opened the panel on a
         pre-selected sold-out variant. */
      if (btn && !btn.classList.contains('sp-missing')) {
        this._selectSize(btn);
      }
    }

    /* ── called by the custom element when variant changes on the page ── */
    onVariantChange(variant) {
      if (!variant) return;
      this._syncOptionsFromVariantId(variant.id);
      this._updateOptionLabels(variant.options);

      /* update price in the mobile info strip */
      const priceEl = this.root.querySelector('#sticky-mobile-price-' + this.sid);
      if (priceEl) {
        const priceSpan = priceEl.querySelector('.price-item--regular, .price-item--sale');
        if (priceSpan) priceSpan.textContent = Shopify.formatMoney(variant.price, window.Shopify && window.Shopify.money_format);
      }

      /* reset any open panel selection since the colour changed */
      this._chosenSize = null;
      if (this.sizeGrid) {
        this.sizeGrid.querySelectorAll('.sticky-sz-btn').forEach(b => b.classList.remove('sp-chosen'));
      }
      /* Reset the confirm button out of any Notify-me state — user
         has to pick a size under the new colour before it can flip. */
      this._updateConfirmMode(null);
    }
  }

  /* ── AMP Back-in-Stock click proxy ──
     Click forwarding for `.sticky-notify-btn` (and the main theme
     `.product-form__notify` button) lives in buy-buttons.liquid — it
     targets AMP's hidden auto-injected #BIS_trigger.action_button. No
     separate listener needed here. */

  /* ── Wire StickyPanel into the existing custom element ──
     We extend connectedCallback to initialise the panel after
     the existing scroll/variant logic is set up. */
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('product-add-to-cart-sticky').forEach(function (el) {
      if (el._stickyPanel) return; // already initialised
      el._stickyPanel = new StickyPanel(el);

      /* Patch the existing element's handleVariantChange to also
         notify our panel. We wrap it rather than replace it. */
      const original = el.handleVariantChange
        ? el.handleVariantChange.bind(el)
        : null;

      el.handleVariantChange = function (event) {
        if (original) original(event);
        /* Notify panel with the new variant object */
        const variant = event && event.data && event.data.variant;
        if (variant && el._stickyPanel) {
          el._stickyPanel.onVariantChange(variant);
        }
      };
    });
  });

})();